<script lang="ts">
export default {
  name: 'ManageSavedFiltersModal',
};
</script>

<script setup lang="ts">
import { ref } from 'vue';
import AppModal from '@/components/v2/AppModal.vue';
import savedPolicyFilterService from '@/services/v2/SavedPolicyFilterService';
import type { SavedPolicyFilter } from '@/types/v2/SavedPolicyFilter';
import { Pencil, Check, Trash2 } from '@lucide/vue';

defineProps<{
  show: boolean;
  filters: SavedPolicyFilter[];
}>();

const emit = defineEmits<{
  (e: 'updated'): void;
  (e: 'cancel'): void;
}>();

// Per-row state
const editingId = ref<number | null>(null);
const editingName = ref('');
const renameErrors = ref<Record<number, string>>({});
const deletingId = ref<number | null>(null);

function startRename(filter: SavedPolicyFilter) {
  editingId.value = filter.id;
  editingName.value = filter.name;
  renameErrors.value = { ...renameErrors.value, [filter.id]: '' };
  deletingId.value = null;
}

function cancelRename() {
  editingId.value = null;
  editingName.value = '';
}

async function confirmRename(filter: SavedPolicyFilter) {
  const trimmed = editingName.value.trim();
  if (!trimmed) {
    renameErrors.value = { ...renameErrors.value, [filter.id]: 'Name is required' };
    return;
  }

  try {
    await savedPolicyFilterService.update(filter.id, {
      name: trimmed,
      filters: filter.filters,
      columns: filter.columns,
    });
    editingId.value = null;
    editingName.value = '';
    emit('updated');
  } catch (error: unknown) {
    if (error instanceof Error && (error.cause as { status?: number })?.status === 400) {
      renameErrors.value = { ...renameErrors.value, [filter.id]: 'A saved filter with this name already exists' };
    } else {
      renameErrors.value = { ...renameErrors.value, [filter.id]: 'An error occurred. Please try again.' };
    }
  }
}

function promptDelete(filter: SavedPolicyFilter) {
  deletingId.value = filter.id;
  editingId.value = null;
}

function cancelDelete() {
  deletingId.value = null;
}

async function confirmDelete(filter: SavedPolicyFilter) {
  try {
    await savedPolicyFilterService.remove(filter.id);
    deletingId.value = null;
    emit('updated');
  } catch {
    deletingId.value = null;
  }
}

function handleCancel() {
  editingId.value = null;
  deletingId.value = null;
  renameErrors.value = {};
  emit('cancel');
}
</script>

<template>
  <AppModal
    :show="show"
    title="Manage Saved Filters"
    :handleCancel="handleCancel"
    :showOverflow="true"
    size="sm">
    <template #content>
      <div class="flex flex-col divide-y divide-gray-100">
        <div
          v-for="filter in filters"
          :key="filter.id"
          class="flex flex-col gap-1 py-2">
          <!-- Normal row -->
          <div
            v-if="editingId !== filter.id && deletingId !== filter.id"
            class="flex items-center justify-between gap-2">
            <span class="truncate text-sm text-gray-800">{{ filter.name }}</span>
            <div class="flex shrink-0 items-center gap-1">
              <button
                class="btn btn-ghost btn-xs p-1"
                title="Rename"
                @click="startRename(filter)">
                <Pencil class="h-3.5 w-3.5 stroke-gray-500" />
              </button>
              <button
                class="btn btn-ghost btn-xs p-1"
                title="Delete"
                @click="promptDelete(filter)">
                <Trash2 class="h-3.5 w-3.5 stroke-error" />
              </button>
            </div>
          </div>

          <!-- Rename row -->
          <div
            v-else-if="editingId === filter.id"
            class="flex flex-col gap-1">
            <div class="flex items-center gap-2">
              <input
                v-model="editingName"
                type="text"
                class="input input-sm flex-1"
                :class="{ 'input-error': renameErrors[filter.id] }"
                @keyup.enter="confirmRename(filter)"
                @keyup.escape="cancelRename" />
              <button
                class="btn btn-ghost btn-xs p-1"
                title="Confirm rename"
                @click="confirmRename(filter)">
                <Check class="h-3.5 w-3.5 stroke-success" />
              </button>
              <button
                class="btn btn-ghost btn-xs p-1"
                title="Cancel"
                @click="cancelRename">
                <Trash2 class="h-3.5 w-3.5 stroke-gray-400" />
              </button>
            </div>
            <p v-if="renameErrors[filter.id]" class="text-xs text-error">{{ renameErrors[filter.id] }}</p>
          </div>

          <!-- Delete confirmation row -->
          <div
            v-else-if="deletingId === filter.id"
            class="flex items-center justify-between gap-2">
            <span class="text-sm text-gray-700">Delete "{{ filter.name }}"?</span>
            <div class="flex shrink-0 items-center gap-1">
              <button
                class="btn btn-xs btn-error text-white"
                @click="confirmDelete(filter)">
                Delete
              </button>
              <button
                class="btn btn-xs btn-ghost"
                @click="cancelDelete">
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template #actions>
      <button
        class="btn btn-sm"
        @click="handleCancel">
        Close
      </button>
    </template>
  </AppModal>
</template>
